<template>
    <nav class="bg-black w-screen z-50 relative">
        <div class="max-w-page mx-auto w-full" id='navContainer'>
            <div class="grid grid-cols-2 w-full lg:flex relative">
                <NuxtLink
                    no-prefetch
                    class="group h-16 lg:h-20 flex items-center grow-0 px-4 cursor-pointer"
                    to="/leads"
                    @mouseover="$refs.lottie.play()"
                >
                    <ClientOnly>
                        <Vue3Lottie
                            class="max-w-[30px] !mx-0"
                            ref="lottie"
                            :animationData="ZapoLogoJson"
                            :autoPlay="false"
                            :height="30"
                            :loop="true"
                            :noMargin="true"
                            :playOnHover="false"
                            :width="30"
                            @onLoopComplete="$refs?.lottie?.stop()"
                        />
                    </ClientOnly>

                    <IconZapoLogoWhiteNoIcon class="h-[21px] order-1 mt-[7px] ml-1 fill-white" />
                </NuxtLink>

                <!-- hidden lg:flex ml-8 h-20 overflow-visible flex items-center -->
                <TransitionHeight
                    :visable="open || openNotifications"
                    class="col-span-2 bg-black order-10 overflow-hidden w-full lg:order-2 lg:!h-20"
                    contentClass="lg:flex items-center lg:h-20"
                >
                    <NuxtLink no-prefetch class="nav-link" to="/leads">
                        <p>Leads</p>
                    </NuxtLink>
                    <template v-if="user.can()">
                        <NuxtLink no-prefetch class="nav-link" to="/prospects">
                            <p>Prospects</p>
                        </NuxtLink>
                        <NuxtLink no-prefetch class="nav-link" to="/deals/pipeline">
                            <p>Deals</p>
                        </NuxtLink>
                        <NuxtLink no-prefetch class="nav-link" to="/tasks" v-if="user.can('plus')">
                            <p>Tasks</p>
                        </NuxtLink>
                        <NuxtLink no-prefetch class="nav-link" to="/events" v-if="user.can('plus')">
                            <p>Events</p>
                        </NuxtLink>
                        <NuxtLink no-prefetch class="nav-link" to="/calendar" v-if="user.can('plus')">
                            <p>Calendar</p>
                        </NuxtLink>
                        <!-- <NuxtLink no-prefetch class="nav-link" to="/inbox" v-if="user.can('plus')">
                            <p>Inbox</p>
                        </NuxtLink> -->
                        <NuxtLink no-prefetch class="nav-link" to="/reporting/leads">
                            <p>Reporting</p>
                        </NuxtLink>
                    </template>
                    <NuxtLink
                        :class="{
                            'router-link-active': $route.path.indexOf('/settings/') >= 0,
                        }"
                        class="nav-link"
                        to="/settings/subscriptions"
                    >
                        <p>Settings</p>
                    </NuxtLink>

                    <div class="px-4 w-full lg:hidden">
                        <div class="border-b border-white/[0.15] w-full mt-2" />
                    </div>

                    <div class="py-2 lg:hidden">
                        <NuxtLink no-prefetch class="nav-link" to="/leads">
                            <IconUser class="w-6 h-6" />
                            <p class="pl-2 text-white">Profile</p>
                        </NuxtLink>

                        <button class="nav-link" @click="logout">
                            <IconLogOut class="w-6 h-6" />
                            <p class="pl-2 text-white">Logout</p>
                        </button>
                    </div>

                    <template v-if="openNotifications">
                        <div class="px-4 text-white">
                            <div class="border-t border-white/20 flex items-center justify-between pt-4 pb-[14px]">
                                <p class="font-semibold text-15">Notifications</p>

                                <NuxtLink
                                    no-prefetch
                                    class="underline text-white text-12"
                                    data-close
                                    to="/notifications"
                                    v-text="'All notifications'"
                                />
                            </div>

                            <div
                                v-for="(notification, ni) in user.notifications"
                                :key="ni"
                                class="flex py-4 border-t border-white/20"
                            >
                                <Notification :notification="notification" />
                            </div>
                        </div>

                        <NuxtLink
                            no-prefetch
                            class="!h-14 !px-6 !text-14 text-white bg-white-t-15 text-left -mx-2  flex items-center !w-auto"
                            data-close
                            to="/notifications"
                        >
                            <p>See all notifications</p>
                        </NuxtLink>
                    </template>
                </TransitionHeight>

                <div class="flex items-center justify-end order-3 px-2 shrink-0 gap-1 lg:gap-0">
                    <Search />

                    <NuxtLink no-prefetch class="w-10 h-10 flex items-center justify-center shrink-0 icon-hover-grey-to-white" tag="button" to="/inbox">
                        <IconArchive class="h-[24px] w-[24px]" />
                    </NuxtLink>

                    <button id="nav-button" class="w-10 h-10 flex items-center justify-center lg:hidden shrink-0" @click="openNotifications = !openNotifications; open = false;">
                        <IconBell v-if="!openNotifications" class="h-[22px] w-[22px] stroke-grey-900" />
                        <IconClose v-else class="h-5 w-5 stroke-white" />
                    </button>

                    <button id="nav-button" class="w-10 h-10 flex items-center justify-center lg:hidden shrink-0" @click="open = !open; openNotifications = false;">
                        <IconMenu v-if="!open" class="h-6 w-6 stroke-grey-900" />
                        <IconClose v-else class="h-5 w-5 stroke-white" />
                    </button>

                    <DropdownV2 id="user-menu" class="hidden lg:block shrink-0" forceSide="right">
                        <template #button="{ open }">
                            <div class="flex items-center border-l border-grey-500 border-opacity-20 shrink-0 ml-6 pl-6">
                                <div class='text-left'>
                                    <div class='flex items-center'>
                                        <p v-if="user != null" class="text-white text-15 font-semibold">
                                            {{ user.fullName() }}
                                        </p>

                                        <IconArrowDown :class="{ 'rotate-180': open }" class="h-4.5 w-4.5 ml-3 transition-all stroke-white" />
                                    </div>

                                    <NuxtLink v-if='user.crm && user.crm.current_ad_period != null' 
                                        :to="{ name: 'settings-subscription', params: { id: user.crm.id } }" 
                                        class="text-grey-700 text-13 underline link-text" @click.stop
                                    >
                                        {{ $help.dp(user.crm_balance) }}
                                    </NuxtLink>
                                </div>
                                

                                <div
                                    class="ml-7 relative hidden w-12 h-12 bg-grey-300 rounded-tl-lg rounded-br-lg lg:flex items-center justify-center"
                                >
                                    <IconUser class="h-6 w-6 stroke-grey-700" v-if='user.profile_image == null' />
                                    <img :src="user.profile_image" class="w-full h-full object-cover rounded-tl-lg rounded-br-lg" v-else />

                                    <div
                                        :class="[user.notifications_unread >= 100 ? 'px-2' : 'w-5']"
                                        class="text-white bg-green-500 rounded-full h-5 text-center font-medium text-11 absolute top-0 right-[30px] leading-[20px]"
                                        v-text="user.notifications_unread"
                                    />
                                </div>
                            </div>
                        </template>

                        <template #menu>
                            <div class="bg-black w-[398px] table-multi-select-menu !pb-0">
                                <NuxtLink no-prefetch class="flex items-center justify-between" data-close tag="button" to="/settings/profile">
                                    <div class="flex items-center">
                                        <IconUser class="w-6 h-6 stroke-grey-800" />
                                        <p class="text-white text-15 font-semibold pl-4">Profile</p>
                                    </div>

                                    <IconArrowRight class="w-4.5 h-4.5 stroke-white" />
                                </NuxtLink>

                                <button id="logout" class="mt-2" @click="logout" data-close>
                                    <IconLogOut class="w-6 h-6 stroke-grey-800" />
                                    <p class="pl-4 text-white text-15 font-semibold">Logout</p>
                                </button>

                                <div class="px-4 mt-6">
                                    <div class="border-t border-white/20 flex items-center justify-between pt-4 pb-[14px]">
                                        <p class="font-semibold text-15">Notifications</p>

                                        <NuxtLink
                                            no-prefetch
                                            class="underline text-white text-12"
                                            data-close
                                            to="/notifications"
                                            v-text="'All notifications'"
                                        />
                                    </div>

                                    <div
                                        v-for="(notification, ni) in user.notifications"
                                        :key="ni"
                                        class="flex py-4 border-t border-white/20"
                                    >
                                        <Notification :notification="notification" />
                                    </div>
                                </div>

                                <NuxtLink
                                    no-prefetch
                                    class="!h-14 !px-6 !text-14 text-white bg-white-t-15 text-left -mx-2 flex items-center !w-auto"
                                    data-close
                                    to="/notifications"
                                >
                                    <p>See all notifications</p>
                                </NuxtLink>
                            </div>
                        </template>
                    </DropdownV2>
                </div>
            </div>
        </div>
    </nav>
</template>

<script>
import Search from '~/components/customer/navs/components/search.vue'
import ZapoLogoJson from '~/assets/animation-zapo-logo.json'
import Notification from '~/components/customer/navs/components/notification.vue'
export default {
    data() {
        return {
            ZapoLogoJson,
            open: false,
            openNotifications: false,
            isDark: false,
        }
    },

    created() {
        this.setDark()
    },

    methods: {
        logout() {
            this.$pop('Confirm', {
                options: {
                    head: 'Log out',
                    message: 'Are you sure you want to log out of Zapo CRM?',
                    button: 'Log out',
                    submitFunction: () => this.$authSanctum.logout(),
                },
            })
        },

        setDark() {
            if (this.isDark) {
                document.documentElement.classList.add('dark')
            } else {
                document.documentElement.classList.remove('dark')
            }
        },
    },

    computed: {
        user() {
            return this.$authSanctum.getUser()
        },
    },

    watch: {
        $route() {
            this.open = false
            this.openNotifications = false
        },
    },

    components: {
        Search: Search,
        Notification
    },
}
</script>
